import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { CheckCircle, Clock, Loader2, ClipboardList, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { format, isPast, isToday, parseISO } from 'date-fns';

const statusColors = {
  pending: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
};

// Helper to format date and time
const formatDueDateTime = (task) => {
  if (!task.due_date) return null;
  
  const dateStr = format(new Date(task.due_date), 'MMM d');
  const timeStr = task.due_time ? ` at ${task.due_time}` : '';
  return `${dateStr}${timeStr}`;
};

// Check if task is overdue
const isOverdue = (task) => {
  if (!task.due_date || task.status === 'completed') return false;
  
  const dueDate = new Date(task.due_datetime || task.due_date);
  return isPast(dueDate) && !isToday(dueDate);
};

// Check if task is due today
const isDueToday = (task) => {
  if (!task.due_date) return false;
  return isToday(new Date(task.due_date));
};

const TasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await api.get(`/tasks?${params.toString()}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCompleteTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'completed' });
      toast.success('Task completed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleUpdateStatus = async (taskId, status) => {
    try {
      await api.put(`/tasks/${taskId}`, { status });
      toast.success('Task updated');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const pendingTasks = tasks
    .filter((t) => t.status !== 'completed')
    .sort((a, b) => {
      // Sort overdue first, then by due_datetime
      const aOverdue = isOverdue(a);
      const bOverdue = isOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      
      // Then by due datetime
      const aDate = a.due_datetime || a.due_date || '9999';
      const bDate = b.due_datetime || b.due_date || '9999';
      return aDate.localeCompare(bDate);
    });
  const completedTasks = tasks.filter((t) => t.status === 'completed');
  
  const overdueTasks = pendingTasks.filter(isOverdue);
  const dueTodayTasks = pendingTasks.filter(t => isDueToday(t) && !isOverdue(t));

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Tasks</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your assigned tasks</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="task-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : tasks.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No tasks found</p>
            <p className="text-sm text-slate-400 mt-1">
              Tasks will appear here when assigned to clients
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending/In Progress Tasks */}
          <Card data-testid="pending-tasks-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                Pending Tasks ({pendingTasks.length})
                {overdueTasks.length > 0 && (
                  <span className="text-sm font-normal text-red-600">
                    ({overdueTasks.length} overdue)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingTasks.length === 0 ? (
                <p className="text-center text-slate-500 py-8">All caught up!</p>
              ) : (
                <div className="space-y-3">
                  {pendingTasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-4 rounded-lg border transition-shadow ${
                        isOverdue(task) 
                          ? 'border-red-300 bg-red-50 hover:shadow-md' 
                          : isDueToday(task)
                          ? 'border-amber-300 bg-amber-50 hover:shadow-md'
                          : 'border-slate-200 bg-white hover:shadow-sm'
                      }`}
                      data-testid={`pending-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">{task.title}</p>
                            {isOverdue(task) && (
                              <Badge className="bg-red-100 text-red-700 text-xs" variant="secondary">
                                Overdue
                              </Badge>
                            )}
                            {isDueToday(task) && !isOverdue(task) && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs" variant="secondary">
                                Due Today
                              </Badge>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-3">
                            <Link
                              to={`/clients/${task.client_id}`}
                              className="text-xs text-accent hover:underline flex items-center gap-1"
                            >
                              {task.client_name}
                              <ArrowRight className="h-3 w-3" />
                            </Link>
                            {task.due_date && (
                              <span className={`text-xs flex items-center gap-1 ${
                                isOverdue(task) ? 'text-red-600 font-medium' : 'text-slate-400'
                              }`}>
                                <Clock className="h-3 w-3" />
                                Due {formatDueDateTime(task)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={task.status}
                            onValueChange={(value) => handleUpdateStatus(task.id, value)}
                          >
                            <SelectTrigger className="h-8 w-28" data-testid={`task-status-${task.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCompleteTask(task.id)}
                            className="text-green-600 hover:text-green-700"
                            data-testid={`complete-task-${task.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed Tasks */}
          <Card data-testid="completed-tasks-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Completed ({completedTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {completedTasks.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No completed tasks</p>
              ) : (
                <div className="space-y-3">
                  {completedTasks.slice(0, 10).map((task) => (
                    <div
                      key={task.id}
                      className="p-4 rounded-lg border border-slate-100 bg-slate-50"
                      data-testid={`completed-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-slate-400 line-through">{task.title}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <Link
                              to={`/clients/${task.client_id}`}
                              className="text-xs text-slate-400 hover:text-accent flex items-center gap-1"
                            >
                              {task.client_name}
                            </Link>
                          </div>
                        </div>
                        <Badge className={statusColors.completed} variant="secondary">
                          Done
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
